import { Users, Trophy, Medal, Award, Loader2 } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { useAuth } from '@/contexts/AuthContext';
import { useQuery } from '@tanstack/react-query';
import { fetchTeamPerformance } from '@/lib/api/simulation';

interface TeamPerformanceTabProps {
  agentId: string;
}

export function TeamPerformanceTab({ agentId }: TeamPerformanceTabProps) {
  const { user, hasRole } = useAuth();
  
  // Only show for sales_rep and company_admin
  if (!hasRole('sales_rep') && !hasRole('company_admin')) {
    return (
      <div className="text-center py-12">
        <Users size={64} className="mx-auto text-muted-foreground mb-4" />
        <p className="text-xl text-muted-foreground">
          Team performance tracking is available for sales representatives and company admins
        </p>
      </div>
    );
  }

  const { data: teamPerformance, isLoading } = useQuery({
    queryKey: ['teamPerformance', agentId, user?.company_id],
    queryFn: () => fetchTeamPerformance(agentId, user?.company_id),
    refetchInterval: 30000, // Auto-refresh every 30 seconds
  });

  if (isLoading) {
    return (
      <div className="flex justify-center items-center p-12">
        <Loader2 size={48} className="text-primary animate-spin" />
      </div>
    );
  }

  if (!teamPerformance || teamPerformance.length === 0) {
    return (
      <div className="text-center p-12">
        <Users size={64} className="text-muted-foreground mx-auto mb-4" />
        <p className="text-xl text-muted-foreground mb-2">No team performance data yet</p>
        <p className="text-muted-foreground">Complete simulations to see team rankings!</p>
      </div>
    );
  }

  const getRankIcon = (rank: number) => {
    if (rank === 1) return <Trophy className="w-6 h-6 text-yellow-500" />;
    if (rank === 2) return <Medal className="w-6 h-6 text-gray-400" />;
    if (rank === 3) return <Award className="w-6 h-6 text-amber-600" />;
    return <div className="w-6 h-6 flex items-center justify-center font-semibold text-muted-foreground">#{rank}</div>;
  };

  return (
    <div className="space-y-8">
      {/* Team Leaderboard */}
      <Card className="p-6">
        <h3 className="text-2xl font-bold text-foreground mb-4">Team Leaderboard</h3>
        <p className="text-sm text-muted-foreground mb-6">
          Top performers for this simulation across your organization
        </p>

        <div className="space-y-3">
          {teamPerformance.map((person, index) => {
            const isCurrentUser = person.auth_user_id === user?.id;
            return (
              <div
                key={person.id}
                className={`flex items-center gap-4 p-4 rounded-lg border ${
                  index < 3 ? 'bg-primary/5 border-primary/20' : 'bg-muted border-border'
                } ${isCurrentUser ? 'ring-2 ring-primary' : ''}`}
              >
                {/* Rank */}
                <div className="flex-shrink-0">
                  {getRankIcon(index + 1)}
                </div>

                {/* Avatar */}
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-primary-subtle to-primary/10 flex items-center justify-center font-bold text-primary flex-shrink-0">
                  {person.user_name?.split(' ').map(n => n[0]).join('') || '?'}
                </div>

                {/* User Info */}
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-foreground truncate flex items-center gap-2">
                    {person.user_name}
                    {isCurrentUser && (
                      <span className="text-xs bg-primary text-primary-foreground px-2 py-1 rounded">You</span>
                    )}
                  </div>
                  <div className="text-sm text-muted-foreground truncate">{person.user_email}</div>
                </div>

                {/* Stats */}
                <div className="flex items-center gap-6 flex-shrink-0">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-primary">{Math.round(person.average_score)}</div>
                    <div className="text-xs text-muted-foreground">Avg</div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg font-semibold text-success">{person.best_score}</div>
                    <div className="text-xs text-muted-foreground">Best</div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg font-semibold text-foreground">{person.total_sessions}</div>
                    <div className="text-xs text-muted-foreground">Sessions</div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </Card>
    </div>
  );
}
